import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Sparkles, Pencil, Check, X, Trash2, Edit2, FileText } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { calculateLineTotals } from "@/utils/estimateCalculations";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface JobEstimate {
  id: string;
  label: string | null;
  status: string;
  total_squares: number;
  customer_price: number;
  cost_subtotal: number;
  overhead_amount: number;
  profit_amount: number;
  material_rate: number;
  labor_rate: number;
  tear_off_cost: number;
  dump_fee: number;
  created_at: string;
  measurement_id: string | null;
}

interface LineItem {
  id: string;
  item_name: string;
  description: string | null;
  quantity: number;
  unit_type: string;
  material_cost: number;
  labor_cost: number;
  total_cost: number;
  category: string | null;
}

interface EstimatesTabProps {
  jobId: string;
}

export const EstimatesTab = ({ jobId }: EstimatesTabProps) => {
  const { orgId } = useAuth();
  const [estimates, setEstimates] = useState<JobEstimate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEstimate, setSelectedEstimate] = useState<JobEstimate | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [generating, setGenerating] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingLineItem, setEditingLineItem] = useState<LineItem | null>(null);
  const [catalogItems, setCatalogItems] = useState<any[]>([]);
  const [measurementData, setMeasurementData] = useState<any | null>(null);
  const [catalogSearch, setCatalogSearch] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    loadEstimates();
    if (orgId) loadCatalogItems();

    const handleEstimateCreated = () => {
      console.log('🔄 Refreshing estimates...');
      loadEstimates();
    };

    window.addEventListener('estimate-created', handleEstimateCreated);
    return () => window.removeEventListener('estimate-created', handleEstimateCreated);
  }, [jobId, orgId]);

  useEffect(() => {
    if (selectedEstimate) {
      loadLineItems(selectedEstimate.id);
      // Load measurement data if available
      if (selectedEstimate.measurement_id) {
        loadMeasurementData(selectedEstimate.measurement_id);
      } else {
        setMeasurementData(null);
      }
    }
  }, [selectedEstimate]);

  const loadMeasurementData = async (measurementId: string) => {
    try {
      const { data, error } = await supabase
        .from("measurements_roof")
        .select("*")
        .eq("id", measurementId)
        .single();

      if (error) throw error;
      setMeasurementData(data);
    } catch (error) {
      console.error("Error loading measurement data:", error);
      setMeasurementData(null);
    }
  };

  const loadCatalogItems = async () => {
    if (!orgId) return;
    
    try {
      const { data, error } = await supabase
        .from("catalog_items")
        .select("*")
        .eq("org_id", orgId)
        .eq("is_active", true)
        .order("name", { ascending: true });

      if (error) throw error;
      setCatalogItems(data || []);
    } catch (error) {
      console.error("Error loading catalog:", error);
    }
  };

  const loadEstimates = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("job_estimates")
        .select("*")
        .eq("job_id", jobId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      setEstimates(data || []);
      if (data && data.length > 0 && !selectedEstimate) {
        setSelectedEstimate(data[0]);
      }
    } catch (error) {
      console.error("Error loading estimates:", error);
      toast.error("Failed to load estimates");
    } finally {
      setLoading(false);
    }
  };

  const loadLineItems = async (estimateId: string) => {
    try {
      const { data, error } = await supabase
        .from("estimate_line_items")
        .select("*")
        .eq("estimate_id", estimateId)
        .order("line_order", { ascending: true });

      if (error) throw error;
      setLineItems(data || []);
    } catch (error) {
      console.error("Error loading line items:", error);
      toast.error("Failed to load line items");
    }
  };

  const handleAISuggest = async () => {
    if (!selectedEstimate || !selectedEstimate.measurement_id) {
      toast.error("No measurement data found for this estimate");
      return;
    }

    try {
      setGenerating(true);
      toast.info("AI is analyzing measurements and matching catalog items...");

      const { data: measurement } = await supabase
        .from("measurements_roof")
        .select("*")
        .eq("id", selectedEstimate.measurement_id)
        .single();

      const { data, error } = await supabase.functions.invoke("suggest-estimate-items", {
        body: {
          measurement_data: measurement,
          catalog_items: catalogItems,
        },
      });

      if (error) throw error;

      if (data?.suggested_items && data.suggested_items.length > 0) {
        const { error: insertError } = await supabase
          .from("estimate_line_items")
          .insert(
            data.suggested_items.map((item: any, index: number) => ({
              estimate_id: selectedEstimate.id,
              item_name: item.item_name,
              description: item.description || null,
              quantity: item.quantity,
              unit_type: item.unit_type,
              material_cost: item.material_cost || 0,
              labor_cost: item.labor_cost || 0,
              total_cost: item.total_cost,
              category: item.category || 'General',
              catalog_item_id: item.catalog_item_id || null,
              is_auto_generated: true,
              line_order: lineItems.length + index,
            }))
          );

        if (insertError) throw insertError;

        toast.success(`Generated ${data.suggested_items.length} line items from measurements`);
        loadLineItems(selectedEstimate.id);
      } else {
        toast.info("AI didn't return any line items for these measurements. Try again or adjust your catalog.");
      }
    } catch (error: any) {
      console.error("Error generating suggestions:", error);
      toast.error(error.message || "Failed to generate line item suggestions");
    } finally {
      setGenerating(false);
    }
  };

  const handleAddLineItem = async (catalogItem: any) => {
    if (!selectedEstimate) return;

    try {
      const newItem = {
        estimate_id: selectedEstimate.id,
        catalog_item_id: catalogItem.id,
        item_name: catalogItem.name,
        description: catalogItem.description || "",
        quantity: 1,
        unit_type: catalogItem.unit_type || "each",
        material_cost: catalogItem.material_cost || 0,
        labor_cost: catalogItem.labor_cost || 0,
        total_cost: (catalogItem.material_cost || 0) + (catalogItem.labor_cost || 0),
        category: catalogItem.category,
        is_auto_generated: false,
        line_order: lineItems.length,
      };

      const { error } = await supabase.from("estimate_line_items").insert(newItem);

      if (error) throw error;

      toast.success("Item added");
      loadLineItems(selectedEstimate.id);
      setShowAddDialog(false);
    } catch (error) {
      console.error("Error adding item:", error);
      toast.error("Failed to add item");
    }
  };

  const handleUpdateLineItem = async (id: string, updates: Partial<LineItem>) => {
    try {
      const { error } = await supabase
        .from("estimate_line_items")
        .update(updates)
        .eq("id", id);

      if (error) throw error;

      toast.success("Item updated");
      loadLineItems(selectedEstimate!.id);
    } catch (error) {
      console.error("Error updating item:", error);
      toast.error("Failed to update item");
    }
  };

  const handleDeleteLineItem = async (id: string) => {
    try {
      const { error } = await supabase.from("estimate_line_items").delete().eq("id", id);

      if (error) throw error;

      toast.success("Item deleted");
      loadLineItems(selectedEstimate!.id);
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error("Failed to delete item");
    }
  };

  const handleStartEdit = (estimateId: string, currentLabel: string) => {
    setEditingId(estimateId);
    setEditLabel(currentLabel || "");
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;

    try {
      const { error } = await supabase
        .from("job_estimates")
        .update({ label: editLabel })
        .eq("id", editingId);

      if (error) throw error;

      toast.success("Estimate label updated");
      loadEstimates();
      setEditingId(null);
      setEditLabel("");
    } catch (error) {
      console.error("Error updating estimate:", error);
      toast.error("Failed to update estimate");
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditLabel("");
  };

  const totals = calculateLineTotals(lineItems);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading estimates...</div>
      </div>
    );
  }

  if (estimates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="text-center">
          <FileText className="h-12 w-12 mx-auto mb-2 text-muted-foreground" />
          <h3 className="text-lg font-semibold">No Estimates Yet</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Create your first estimate from a measurement
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="lg:w-64 space-y-4">
          {estimates.map((estimate) => (
            <Card
              key={estimate.id}
              className={cn(
                "cursor-pointer transition-all hover:shadow-md",
                selectedEstimate?.id === estimate.id && "ring-2 ring-primary"
              )}
              onClick={() => setSelectedEstimate(estimate)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  {editingId === estimate.id ? (
                    <div className="flex-1 flex items-center gap-2">
                      <Input
                        value={editLabel}
                        onChange={(e) => setEditLabel(e.target.value)}
                        className="h-8"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleSaveEdit(); }}>
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleCancelEdit(); }}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex-1">
                        <h3 className="font-semibold">{estimate.label || "Untitled Estimate"}</h3>
                        <p className="text-xs text-muted-foreground">
                          {new Date(estimate.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={(e) => { e.stopPropagation(); handleStartEdit(estimate.id, estimate.label || ""); }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
                <div className="flex items-center justify-between mt-3">
                  <Badge variant={estimate.status === 'final' ? 'default' : 'secondary'}>
                    {estimate.status === 'final' ? 'Final' : 'Draft'}
                  </Badge>
                  <div className="text-lg font-bold text-primary">
                    ${estimate.customer_price.toLocaleString()}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {selectedEstimate && (
          <div className="flex-1 flex gap-6">
            {/* Linear Measurements Sidebar */}
            {measurementData && (
              <div className="lg:w-64 space-y-4">
                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-sm mb-3">Linear Measurements</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between items-center py-1 border-b border-border/50">
                        <span className="text-muted-foreground">Total Squares:</span>
                        <span className="font-mono font-semibold">{measurementData.total_squares?.toFixed(2) || '0'}</span>
                      </div>
                      <div className="flex justify-between items-center py-1 border-b border-border/50">
                        <span className="text-muted-foreground">Ridges:</span>
                        <span className="font-mono">{measurementData.ridges?.toFixed(1) || '0'} ft</span>
                      </div>
                      <div className="flex justify-between items-center py-1 border-b border-border/50">
                        <span className="text-muted-foreground">Hips:</span>
                        <span className="font-mono">{measurementData.hips?.toFixed(1) || '0'} ft</span>
                      </div>
                      <div className="flex justify-between items-center py-1 border-b border-border/50">
                        <span className="text-muted-foreground">Valleys:</span>
                        <span className="font-mono">{measurementData.valleys?.toFixed(1) || '0'} ft</span>
                      </div>
                      <div className="flex justify-between items-center py-1 border-b border-border/50">
                        <span className="text-muted-foreground">Eaves:</span>
                        <span className="font-mono">{measurementData.eaves?.toFixed(1) || '0'} ft</span>
                      </div>
                      <div className="flex justify-between items-center py-1">
                        <span className="text-muted-foreground">Rakes:</span>
                        <span className="font-mono">{measurementData.rakes?.toFixed(1) || '0'} ft</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            <div className="flex-1">
              <Card>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold mb-1">
                      {selectedEstimate.label || "Untitled Estimate"}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Created {new Date(selectedEstimate.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge variant={selectedEstimate.status === 'final' ? 'default' : 'secondary'}>
                    {selectedEstimate.status === 'final' ? 'Final' : 'Draft'}
                  </Badge>
                </div>

                <div className="space-y-6">
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-sm text-muted-foreground uppercase">
                        Line Items
                      </h3>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleAISuggest}
                          disabled={generating || lineItems.length > 0}
                        >
                          <Sparkles className="h-4 w-4 mr-2" />
                          {generating ? "Generating..." : "AI Suggest"}
                        </Button>
                        <Button size="sm" onClick={() => setShowAddDialog(true)}>
                          <Plus className="h-4 w-4 mr-2" />
                          Add Item
                        </Button>
                      </div>
                    </div>

                    {lineItems.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No line items yet. Click "AI Suggest" to auto-generate or "Add Item" to add manually.
                      </div>
                    ) : (
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Item</TableHead>
                              <TableHead className="text-right">Qty</TableHead>
                              <TableHead>Unit</TableHead>
                              <TableHead className="text-right">Material</TableHead>
                              <TableHead className="text-right">Labor</TableHead>
                              <TableHead className="text-right">Total</TableHead>
                              <TableHead className="w-20"></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {lineItems.map((item) => (
                              <TableRow key={item.id}>
                                <TableCell>
                                  <div className="font-medium">{item.item_name}</div>
                                  {item.description && (
                                    <div className="text-xs text-muted-foreground">{item.description}</div>
                                  )}
                                </TableCell>
                                <TableCell className="text-right">{item.quantity}</TableCell>
                                <TableCell className="text-sm text-muted-foreground">{item.unit_type}</TableCell>
                                <TableCell className="text-right">${item.material_cost.toFixed(2)}</TableCell>
                                <TableCell className="text-right">${item.labor_cost.toFixed(2)}</TableCell>
                                <TableCell className="text-right font-semibold">${item.total_cost.toFixed(2)}</TableCell>
                                <TableCell>
                                  <div className="flex gap-1">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-8 w-8 p-0"
                                      onClick={() => setEditingLineItem(item)}
                                    >
                                      <Edit2 className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                      onClick={() => handleDeleteLineItem(item.id)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        <div className="bg-muted/50 p-4 space-y-2 border-t">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Materials Total:</span>
                            <span className="font-semibold">${totals.materialTotal.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Labor Total:</span>
                            <span className="font-semibold">${totals.laborTotal.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-lg font-bold border-t pt-2">
                            <span>Grand Total:</span>
                            <span className="text-primary">${totals.grandTotal.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Push to Proposal Button */}
            {selectedEstimate && lineItems.length > 0 && (
              <div className="mt-4">
                <Button
                  onClick={() => {
                    navigate(`/jobs/${jobId}?tab=Proposals&createProposal=true&estimateId=${selectedEstimate.id}`);
                  }}
                  className="w-full"
                  size="lg"
                >
                  Push to Proposal ({lineItems.length} items)
                </Button>
              </div>
            )}
          </div>
          </div>
        )}
      </div>

      {/* Add Item Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => {
        setShowAddDialog(open);
        if (!open) setCatalogSearch("");
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Item from Catalog</DialogTitle>
            <DialogDescription>Select an item to add to this estimate</DialogDescription>
          </DialogHeader>
          <div className="mb-4">
            <Input
              placeholder="Search catalog items..."
              value={catalogSearch}
              onChange={(e) => setCatalogSearch(e.target.value)}
              className="w-full"
            />
          </div>
          <div className="max-h-96 overflow-y-auto">
            <div className="space-y-2">
              {catalogItems
                .filter((item) => 
                  item.name.toLowerCase().includes(catalogSearch.toLowerCase()) ||
                  item.category?.toLowerCase().includes(catalogSearch.toLowerCase()) ||
                  item.description?.toLowerCase().includes(catalogSearch.toLowerCase())
                )
                .map((item) => (
                <Card
                  key={item.id}
                  className="p-3 cursor-pointer hover:bg-accent/50 transition-colors"
                  onClick={() => handleAddLineItem(item)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-medium">{item.name}</p>
                      {item.category && (
                        <p className="text-xs text-muted-foreground">{item.category}</p>
                      )}
                      <div className="flex gap-2 mt-1">
                        {item.unit_price && (
                          <p className="text-xs font-semibold">
                            ${item.unit_price}/{item.unit_type || 'ea'}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </Card>
              ))}
              {catalogItems.filter((item) => 
                item.name.toLowerCase().includes(catalogSearch.toLowerCase()) ||
                item.category?.toLowerCase().includes(catalogSearch.toLowerCase()) ||
                item.description?.toLowerCase().includes(catalogSearch.toLowerCase())
              ).length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {catalogSearch ? "No items match your search" : "No catalog items found"}
                </p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Line Item Dialog */}
      {editingLineItem && (
        <Dialog open={!!editingLineItem} onOpenChange={() => setEditingLineItem(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Line Item</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Item Name</Label>
                <Input
                  value={editingLineItem.item_name}
                  onChange={(e) =>
                    setEditingLineItem({ ...editingLineItem, item_name: e.target.value })
                  }
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={editingLineItem.description || ""}
                  onChange={(e) =>
                    setEditingLineItem({ ...editingLineItem, description: e.target.value })
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    value={editingLineItem.quantity}
                    onChange={(e) =>
                      setEditingLineItem({
                        ...editingLineItem,
                        quantity: parseFloat(e.target.value) || 0,
                      })
                    }
                  />
                </div>
                <div>
                  <Label>Unit Type</Label>
                  <Select
                    value={editingLineItem.unit_type}
                    onValueChange={(value) =>
                      setEditingLineItem({ ...editingLineItem, unit_type: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="each">Each</SelectItem>
                      <SelectItem value="sqft">Sqft</SelectItem>
                      <SelectItem value="sq">Square</SelectItem>
                      <SelectItem value="lf">LF</SelectItem>
                      <SelectItem value="hour">Hour</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Material Cost</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingLineItem.material_cost}
                    onChange={(e) =>
                      setEditingLineItem({
                        ...editingLineItem,
                        material_cost: parseFloat(e.target.value) || 0,
                      })
                    }
                  />
                </div>
                <div>
                  <Label>Labor Cost</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingLineItem.labor_cost}
                    onChange={(e) =>
                      setEditingLineItem({
                        ...editingLineItem,
                        labor_cost: parseFloat(e.target.value) || 0,
                      })
                    }
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingLineItem(null)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const quantity = editingLineItem.quantity || 0;
                  const materialCost = editingLineItem.material_cost || 0;
                  const laborCost = editingLineItem.labor_cost || 0;
                  const totalCost = quantity * (materialCost + laborCost);

                  handleUpdateLineItem(editingLineItem.id, {
                    ...editingLineItem,
                    total_cost: totalCost,
                  });
                  setEditingLineItem(null);
                }}
              >
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
